import { css, cx } from '@emotion/css';
import { useEditor, useNode, ROOT_NODE } from '@craftjs/core';

import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import {
  ICON_NODE_NAME,
  ICON_DEFAULT_SIZE,
  ICON_STYLE_REGULAR,
} from '@builder4/shared/utils/constants';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import type { NodeCustomProps } from '@builder4/shared/types/craft';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getPositionStyle,
  getPaddingStyle,
  getMarginStyle,
  getBorderRadiusStyle,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
  rgbaToString,
} from '@builder4/shared/utils/styleHelpers';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import type { StylesConfig } from '@builder4/shared/types/styles';
import { IconPropsDto } from '@builder4/shared/dto/craft/props/IconPropsDto';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { RgbaDto } from '@builder4/shared/dto/styles';

export type IconProps = IconPropsDto;

export const defaultProps: Partial<IconProps> = {
  iconName: 'star',
  iconStyle: ICON_STYLE_REGULAR,
  color: { r: 0, g: 0, b: 0, a: 1 },
  opacity: 1,
  width: `${ICON_DEFAULT_SIZE}px`,
  height: `${ICON_DEFAULT_SIZE}px`,
  aspectRatio: 1,
  aspectRatioLock: true,
  padding: [0, 0, 0, 0],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  backgroundColor: undefined,
  borderColor: undefined,
  borderWidth: 0,
  fieldShadowType: 'none',
  fieldShadowSettings: { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
  position: 'relative',
  top: '0px',
  left: '0px',
  right: undefined,
  bottom: undefined,
  zIndex: 0,
  positionAnchorsState: {
    enableTop: true,
    enableRight: false,
    enableBottom: false,
    enableLeft: true,
    enabled: false,
  },
  visibility: DEFAULT_VISIBILITY,
  tablet: {},
  mobile: {},
};

function computeIconContentStyles(props: Partial<IconProps>, activeGlobalStyles: StylesConfig) {
  const {
    padding,
    borderRadius,
    backgroundColor,
    borderColor,
    borderWidth,
    fieldShadowType,
    fieldShadowSettings,
  } = props;

  const paddingString = padding ? getPaddingStyle(padding as TRBLNumberCOORDS).padding : undefined;
  const borderRadiusString = borderRadius
    ? getBorderRadiusStyle(borderRadius as TRBLNumberCOORDS).borderRadius
    : undefined;
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);
  const borderColorString = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);

  return {
    padding: paddingString,
    borderRadius: borderRadiusString,
    background: background || undefined,
    border:
      borderWidth && borderColorString
        ? `${borderWidth}px solid ${borderColorString}`
        : borderWidth
          ? `${borderWidth}px solid transparent`
          : undefined,
    boxShadow: getBoxShadow(fieldShadowType, fieldShadowSettings),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };
}

const iconContentStyle = (
  props: Partial<IconProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const currentBpStyles = computeIconContentStyles(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      borderRadius: props.borderRadius || [0, 0, 0, 0],
    },
    activeGlobalStyles,
  );
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    boxSizing: 'border-box',
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    pointerEvents: 'auto',
    containerType: 'size',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeIconContentStyles(mobile, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeIconContentStyles(tablet, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
};

const computeIconNodeStyles = (props: Partial<IconProps>) => {
  const { width, height } = props;
  const { margin, zIndex } = props;
  const { aspectRatioLock, aspectRatio } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin).margin : undefined;
  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = aspectRatio ? `${aspectRatio} / 1` : undefined;

  return {
    padding: marginString,
    ...positionStyle,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    width: width || undefined,
    maxWidth: `${ICON_DEFAULT_SIZE}px`,
    zIndex: zIndex || undefined,
    ...(isAspectRatioLocked && aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
};

const iconNodeStyle = (props: Partial<IconProps>, isServerRender: boolean) => {
  const currentBpStyles = computeIconNodeStyles({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
  });
  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
    display: 'flex',
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<IconProps>, props);
    const mobileStyles = computeIconNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<IconProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<IconProps>, props);
    const tabletStyles = computeIconNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<IconProps>);
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles;
  }

  return css(finalStyles);
};

const getColorString = (
  color: RgbaDto | string | undefined,
  activeGlobalStyles: StylesConfig,
): string => {
  if (!color) return 'rgba(0, 0, 0, 1)';

  if (typeof color === 'string') {
    return getGlobalStyleColor(color, activeGlobalStyles.colors);
  }

  return rgbaToString(color);
};

const Icon = (props: Partial<IconProps>) => {
  const mergedProps = {
    ...defaultProps,
    ...props,
  };

  const { isEditable, environment, styles: activeGlobalStyles, iconManager } = useRenderContext();

  const {
    connectors: { connect, drag },
  } = useNode();

  const { breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  const getProp = <K extends keyof IconProps>(key: K) =>
    getBreakpointProp(mergedProps, key, breakpoint);

  const visibility = getProp('visibility');
  const iconName = getProp('iconName');
  const iconStyle = getProp('iconStyle') || ICON_STYLE_REGULAR;
  const color = getProp('color');
  const opacity = getProp('opacity') ?? 1;

  const visibilityClasses = getVisibilityClasses(visibility || DEFAULT_VISIBILITY);
  const isServerRender = environment === 'server';

  const hasIcon = Boolean(iconName && iconManager.hasIcon(iconName));
  const colorString = getColorString(color, activeGlobalStyles);

  if (!hasIcon) return null;

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx('icon-node', visibilityClasses, iconNodeStyle(mergedProps, isServerRender))}
    >
      <div
        className={cx(
          'icon-content',
          isEditable && 'node-dashed-indicator boundary-indicator styled-node',
          iconContentStyle(mergedProps, isServerRender, activeGlobalStyles),
        )}
      >
        {hasIcon && (
          <i
            className={`fa-fw fa-${iconStyle} fa-${iconName}`}
            style={{ color: colorString, opacity, fontSize: 'min(80cqi, 100cqb)' }}
          />
        )}
      </div>
    </div>
  );
};

Icon.craft = {
  displayName: ICON_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: ICON_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

function getIconNodeStylesObject(props: Partial<IconProps>) {
  const currentBpStyles = computeIconNodeStyles({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
  });
  const finalStyles: Record<string, unknown> = {
    ...currentBpStyles,
    display: 'flex',
    mobile: {},
    tablet: {},
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const positionProps = getPositionPropsV2(mobile as Partial<IconProps>, props);
    const mobileStyles = computeIconNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<IconProps>);
    finalStyles.mobile = mobileStyles;
  }
  if (tablet && Object.keys(tablet).length) {
    const positionProps = getPositionPropsV2(tablet as Partial<IconProps>, props);
    const tabletStyles = computeIconNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<IconProps>);
    finalStyles.tablet = tabletStyles;
  }

  return finalStyles;
}

export { getIconNodeStylesObject };
export default Icon;
